import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSubjectiveExam, startSubjectiveExam, autoSaveAnswer, submitSubjectiveExam } from '../services/subjective_exams';
import DigitalWritingBoard from '../components/DigitalWritingBoard';
import { Clock, ChevronLeft, ChevronRight, Send, AlertTriangle, Loader2 } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import jsPDF from 'jspdf';
import api from '../services/api';

export default function StudentSubjectiveExam() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  // Store answers data: questionId -> pageNumber -> canvasData
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const initExam = async () => {
      try {
        const [examData, submissionData] = await Promise.all([
          getSubjectiveExam(examId),
          startSubjectiveExam(examId)
        ]);
        
        if (submissionData.status !== 'in_progress') {
          toast.error("This exam has already been submitted!");
          navigate('/student-subjective-exams');
          return;
        }

        setExam(examData);
        setSubmission(submissionData);
        
        // Timer logic
        const start = new Date(submissionData.created_at).getTime();
        const now = new Date().getTime();
        const elapsed = Math.floor((now - start) / 1000);
        const totalSeconds = examData.duration_minutes * 60;
        const remaining = totalSeconds - elapsed;
        
        setTimeLeft(remaining > 0 ? remaining : 0);
      } catch (err) {
        console.error("Exam load error:", err.response?.data || err);
        toast.error("Failed to load exam. Please contact support.");
        navigate('/student-subjective-exams');
      } finally {
        setLoading(false);
      }
    };
    initExam();
  }, [examId, navigate]);

  useEffect(() => {
    if (!loading && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmit(true); // auto submit
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [loading, timeLeft]);

  const handleSaveAnswer = async (questionId, pageNumber, canvasData) => {
    try {
      await autoSaveAnswer(submission.id, questionId, pageNumber, canvasData);
      setAnswers(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          [pageNumber]: canvasData
        }
      }));
    } catch (err) {
      console.error("Auto-save failed:", err);
      toast.error("Auto-save failed. Check connection.");
    }
  };

  const uploadPdfToS3 = async (pdfBlob) => {
    const formData = new FormData();
    formData.append("file", pdfBlob, `submission_${submission.id}.pdf`);
    const res = await api.post('/upload/file', formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return res.data.url;
  };

  const handleSubmit = async (isAuto = false) => {
    if (!isAuto) {
      if (!window.confirm("Are you sure you want to submit? You cannot change your answers after submission.")) return;
    }
    
    setSubmitting(true);
    const toastId = toast.loading("Generating Answer Sheet PDF...");
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirstPage = true;

      // Iterate through questions and append their canvases to the PDF
      for (const q of exam.questions) {
        const qAnswers = answers[q.id];
        if (qAnswers) {
          const pages = Object.keys(qAnswers).sort((a,b) => a-b);
          for (const page of pages) {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;
            
            // Add Header
            pdf.setFontSize(12);
            pdf.text(`Question: ${q.prompt.substring(0, 50)}...`, 10, 10);
            pdf.text(`Page: ${page}`, 180, 10);
            
            // Add Canvas Image
            const imgData = qAnswers[page];
            // Scale to A4 maintaining aspect ratio roughly
            pdf.addImage(imgData, 'PNG', 10, 20, 190, 250);
          }
        }
      }

      if (isFirstPage) {
        // Blank submission
        pdf.text("No answers submitted.", 10, 10);
      }

      const pdfBlob = pdf.output('blob');
      toast.loading("Uploading to server...", { id: toastId });
      
      const s3Url = await uploadPdfToS3(pdfBlob);
      await submitSubjectiveExam(submission.id, s3Url);
      
      toast.success("Exam submitted successfully!", { id: toastId });
      navigate('/student-subjective-exams');
      
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit exam. Please try again.", { id: toastId });
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>;

  const currentQuestion = exam.questions[currentQuestionIndex];
  const qAnswers = answers[currentQuestion.id] || {};
  
  // Format Time
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
  const timeStr = `${hours > 0 ? hours+':' : ''}${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <Toaster position="top-right" />
      
      {/* Left Pane - Navigator & Timer */}
      <div className="w-64 bg-white border-r flex flex-col shadow-sm z-10">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600 font-bold text-xl">
            <Clock size={20} />
            <span>{timeStr}</span>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <h3 className="font-semibold text-gray-700 mb-4">Questions Navigation</h3>
          <div className="grid grid-cols-4 gap-2">
            {exam.questions.map((q, i) => {
              const hasAnswer = Object.keys(answers[q.id] || {}).length > 0;
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestionIndex(i)}
                  className={`aspect-square flex items-center justify-center rounded-lg font-medium transition ${
                    currentQuestionIndex === i 
                      ? 'bg-indigo-600 text-white shadow-md' 
                      : hasAnswer 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                  }`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50">
          <button 
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="w-full bg-indigo-600 text-white font-medium py-3 rounded-lg shadow-sm hover:bg-indigo-700 transition flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            Submit Exam
          </button>
        </div>
      </div>

      {/* Center Pane - Question Display */}
      <div className="flex-1 flex flex-col min-w-[300px] border-r">
        <div className="p-4 bg-white border-b shadow-sm flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">{exam.title}</h2>
          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
            Marks: {currentQuestion.marks}
          </span>
        </div>
        <div className="flex-1 p-6 bg-gray-50 overflow-y-auto">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500 mb-2 font-medium">Question {currentQuestionIndex + 1} of {exam.questions.length}</div>
            <div className="text-lg text-gray-800 whitespace-pre-wrap leading-relaxed">
              {currentQuestion.prompt}
            </div>
            {currentQuestion.question_type === 'short_answer' && (
              <div className="mt-4 flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg text-sm border border-amber-200">
                <AlertTriangle size={16} /> Short Answer required.
              </div>
            )}
          </div>
        </div>
        <div className="bg-white p-4 border-t flex justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <button 
            onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
            className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium"
          >
            <ChevronLeft size={20} /> Previous
          </button>
          <button 
            onClick={() => setCurrentQuestionIndex(prev => Math.min(exam.questions.length - 1, prev + 1))}
            disabled={currentQuestionIndex === exam.questions.length - 1}
            className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium"
          >
            Next <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Right Pane - Digital Writing Board */}
      <div className="w-[500px] xl:w-[600px] flex flex-col p-4 bg-gray-50">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold text-gray-700">Digital Answer Sheet</h3>
          <span className="text-xs text-gray-500">Page 1 / 1</span>
        </div>
        <div className="flex-1 min-h-0">
          <DigitalWritingBoard 
            key={`q-${currentQuestion.id}-p-1`} // Force re-render completely when question changes to avoid state leaks
            questionId={currentQuestion.id}
            pageNumber={1}
            initialData={qAnswers[1]}
            onSave={handleSaveAnswer}
          />
        </div>
      </div>
    </div>
  );
}
